import {
  DataFeedFileType,
  GetOptionalColumnCatalogResponse,
} from '@cbinsights/externalapiservice/externalapiservice';
import { Checkbox, Collapse, Form } from 'antd';
import { UseQueryResult, useQuery } from 'react-query';
import { DataFeedFormField, fileTypeToNumber } from './DataFeedForm';
import { getOptionalColumnCatalog } from '../services/apis';

const { Panel } = Collapse;

export type OptionalColumnCatalog = Record<number, string[]>;

export const useGetOptionalColumnCatalog = (): UseQueryResult<
  OptionalColumnCatalog,
  Error
> =>
  useQuery<
    GetOptionalColumnCatalogResponse,
    Error,
    OptionalColumnCatalog,
    ['GetOptionalColumnCatalog']
  >({
    queryKey: ['GetOptionalColumnCatalog'],
    queryFn: getOptionalColumnCatalog,
    staleTime: Infinity,
    cacheTime: Infinity,
    select: (data) =>
      Object.fromEntries(
        Object.entries(data.optional_columns_by_file_type).map(
          ([fileType, entry]) => [fileType, Object.keys(entry.columns).sort()]
        )
      ),
  });

const SelectAllColumns = ({
  fileType,
  allColumns,
}: {
  fileType: DataFeedFileType;
  allColumns: string[];
}) => {
  const form = Form.useFormInstance();
  const fieldName = [DataFeedFormField.optionalColumns, fileType];
  const columns: string[] = Form.useWatch(fieldName, form) ?? allColumns;

  return (
    <span
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyPress={(e) => e.stopPropagation()}
    >
      <Checkbox
        checked={columns.length === allColumns.length}
        indeterminate={columns.length > 0 && columns.length < allColumns.length}
        onChange={(e) =>
          form.setFieldValue(fieldName, e.target.checked ? allColumns : [])
        }
      >
        {columns.length} / {allColumns.length} columns
      </Checkbox>
    </span>
  );
};

export const OptionalColumnsSelector = ({
  fileType,
}: {
  fileType: DataFeedFileType;
}) => {
  const { data: catalog } = useGetOptionalColumnCatalog();
  const allColumns = catalog?.[fileTypeToNumber(fileType)];

  if (!allColumns?.length) {
    return null;
  }

  return (
    <Collapse className="ml-6 mt-1">
      <Panel
        key={fileType}
        header="Optional columns"
        extra={<SelectAllColumns fileType={fileType} allColumns={allColumns} />}
        forceRender
      >
        <Form.Item
          name={[DataFeedFormField.optionalColumns, fileType]}
          initialValue={allColumns}
          preserve={false}
          noStyle
        >
          <Checkbox.Group className="w-full" style={{ columns: 2 }}>
            {allColumns.map((col) => (
              <div key={col}>
                <Checkbox value={col}>{col}</Checkbox>
              </div>
            ))}
          </Checkbox.Group>
        </Form.Item>
      </Panel>
    </Collapse>
  );
};
